import { Fragment, useEffect, useMemo, useState } from "react";
import ReactTable from 'react-table';
import { useDispatch, useSelector } from "react-redux";
import { AddUpdateApiConfigurationModal, BackButton, BadgeComponent, Breadcrumbs, DropDownComponent, IconButtonComponent, LoaderComponent, PaginationComponent } from "../../components";
import { VerticalDotsSvg } from "../../assets/svgs";
import { AuthPageContainer, AuthPageHead, ReactTableWrapper, ReactTableWrappers } from "../../styles/global-styles";
import { getApiConfigManagementInboxData, postAddedUpdateApiConfigReview } from "../../redux/slices/api-config-management-inbox-slice";
import { fieldTypesConstant } from "../../constants/app-constants";
import { Menus } from "../../constants/menus-constant";
import { useLocation } from "react-router";
import { getApiConfigValues } from "../../redux/slices/api-config-slice";

const ApiConfigManagementInbox = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { apiConfigAttributeNameOptions } = useSelector((state) => state.apiConfigReducer);
    const { loading, inboxData, pagination } = useSelector((state) => state.ApiConfigManagementInboxReducer);

    const MenuList = Menus({ user });

    const PageIcon = MenuList?.flatMap(i => i?.subMenus)?.filter(i => i?.to === location?.pathname)[0]?.icon;
    const PageTitle = MenuList?.flatMap(i => i?.subMenus)?.filter(i => i?.to === location?.pathname)[0]?.label;

    const isChecker = location.pathname.includes('checker'); // Check if the current path includes 'checker'
    const initialPagination = {
        currentPage: 0,
        pageSize: 20,
    };

    const initialStates = {
        data: [],
        pagination: initialPagination,
        selected: null,
        isDropdownOpen: false,
        isModalOpen: false,
        isUpdate: true,
    };

    const [states, setStates] = useState(initialStates);
    const [form, setForm] = useState();

    const defaultParams = { pagination: states.pagination };

    const getInboxData = async (updatedParams) => {
        if (user && updatedParams) {
            try {
                await Promise.all([
                    dispatch(getApiConfigValues(user)),
                    dispatch(getApiConfigManagementInboxData({ user, ...updatedParams })),
                ]);
            } catch (error) {
                console.error("One or more API calls failed", error);
            }
        }
    };

    // const handleReload = () => {
    //     getInboxData(defaultParams);
    // };

    const handlePagination = (e) => {
        const params = {
            ...defaultParams,
            pagination: {
                ...defaultParams.pagination,
                currentPage: e
            }
        };
        getInboxData(params);
    };

    const handleSelectRow = (row) => {
        if (row === states.selected && states.isDropdownOpen) {
            setStates((old) => ({ ...old, isDropdownOpen: false, selected: null }));
        } else {
            setStates((old) => ({ ...old, isDropdownOpen: true, selected: row }));
        }
    };

    const handleCloseDropdown = () => {
        setStates((old) => ({ ...old, isDropdownOpen: false }));
    };

    const handleModalOpen = () => {
        const { reason, taskDetailRes } = states.selected;
        const existingConfig = taskDetailRes?.apiConfiguration;

        setForm({
            cardInquiry: existingConfig?.cardInquiry ? existingConfig?.cardInquiry?.map(item => ({
                ...item,
                attributeValue: (item?.attributeValue || ''),
                attributeField: apiConfigAttributeNameOptions.some(option => option.value === item?.attributeName) ? fieldTypesConstant?.dropdown : fieldTypesConstant?.text,
            })) : [],
            otpGenerator: existingConfig?.otpGenerator ? existingConfig?.otpGenerator?.map(item => ({
                ...item,
                otpDelivery: (item?.otpDelivery?.id || ''),
                attributeValue: (item?.attributeValue || ''),
                attributeField: apiConfigAttributeNameOptions.some(option => option.value === item?.attributeName) ? fieldTypesConstant?.dropdown : fieldTypesConstant?.text,
            })) : [],
            otpValidator: existingConfig?.otpValidator ? existingConfig?.otpValidator?.map(item => ({
                ...item,
                attributeValue: (item?.attributeValue || ''),
                attributeField: apiConfigAttributeNameOptions.some(option => option.value === item?.attributeName) ? fieldTypesConstant?.dropdown : fieldTypesConstant?.text,
            })) : [],
            reason,
        });

        setStates((old) => ({ ...old, isModalOpen: true }));
    };

    const handleModalClose = () => {
        setStates((old) => ({ ...old, isModalOpen: false, selected: null }));
    };

    const handleFormSubmit = (payloads) => {
        const { operation, processId } = states.selected;
        const { apiConfiguration, ...payload } = payloads;

        const updatedPayload = {
            ...payload,
            apiConfigData: { apiConfiguration, institution: states?.selected?.taskDetailRes?.institution },
            operation,
            processId,
        };

        dispatch(postAddedUpdateApiConfigReview({ user, body: updatedPayload })).then((action) => {
            if (action?.payload?.status === 201 || action?.payload?.status === 200) {
                handleModalClose();
                getInboxData(defaultParams);
            }
        });
    };

    const actions = [
        { label: isChecker ? 'Review' : 'Edit', action: () => handleModalOpen() },
    ];

    useEffect(() => {
        getInboxData(defaultParams);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (inboxData && Array.isArray(inboxData)) {
            setStates((old) => ({ ...old, data: inboxData, pagination: pagination }));
        }
    }, [inboxData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown')) {
                handleCloseDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // TABLE COLUMNS SETUP AS PER DATA
    const columns = useMemo(() => [
        { 
            accessor: 'reqInitDateTime', 
            Header: 'Request Date', 
            minWidth: 170,
            style: {
                textAlign: 'center'
            },
            sortable: false, 
            filterable: false 
        },
        {
            accessor: 'processId', Header: 'Process ID', minWidth: 150,
            style: {
                textAlign: 'center'
            },
            sortable: false, filterable: false
        },
        {
            accessor: 'institution',
            Header: 'Participant Name',
            minWidth: 200,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => row.original.taskDetailRes?.institution?.descr || '',
            sortable: false,
            filterable: false,
        },
        {
            accessor: 'operation',
            Header: 'Operation',
            minWidth: 170,
            style: {
                textAlign: 'center',
            },
            Cell: (row) => {
                const operation = row.original.operation?.toLowerCase();
                let badgeType = 'default';

                if (operation?.includes('add configuration')) {
                    badgeType = 'default';
                } else if (operation?.includes('update configuration')) {
                    badgeType = 'warning';
                }

                return (
                    <>
                        {
                            row.original.operation &&
                            <BadgeComponent label={row.original.operation} type={badgeType} />
                        }
                    </>
                );
            },
            sortable: false,
            filterable: false,
        },
        { 
            accessor: 'createdBy', 
            Header: 'Created / Modified By', 
            minWidth: 160,
            style: {
                textAlign: 'center'
            },
            sortable: false, 
            filterable: false 
        },
        {
            accessor: 'isaccepted',
            Header: 'Status',
            minWidth: 120,
            style: {
                textAlign: 'center',
            },
            Cell: (row) => {
                return (
                    <>
                        {
                            ([true, false].includes(row.original.isaccepted) && row.original.reason) ? (
                                <BadgeComponent label={row.original.isaccepted ? 'Accepted' : 'Rejected'} type={row.original.isaccepted ? 'success' : 'error'} />
                            ) : (
                                <BadgeComponent label='Pending' type='default' />
                            )
                        }
                    </>
                )
            },
            sortable: false,
            filterable: false,
        },
        { 
            accessor: 'reason', 
            Header: 'Comments', 
            minWidth: 170,
            style: {
                textAlign: 'center'
            },
            sortable: false, 
            filterable: false 
        },
        
        {
            accessor: 'action',
            Header: 'Action',
            minWidth: 100,
            // style: {
            //     height: 45, display: 'flex', justifyContent: 'flex-end', alignItems: 'center'
            // },
            Cell: (row) => {
                return (
                    <div className="action-dropdown" style={{ position: 'absolute' }}>
                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row.original)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow(row.original)} />
                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row.original} close={handleCloseDropdown} actions={actions} />
                    </div>
                )
            },
            sortable: false,
            filterable: false,
        },
    ], [ states.isDropdownOpen, states.selected ]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AuthPageContainer $heightfull>
            {/* BACK BUTTON */}
            <BackButton />
            {/* PAGE HEAD */}
            <AuthPageHead>
                <div className="left">
                    <PageIcon className="page-icon" />
                    <h1>Inbox</h1>
                    {/* <IconButtonComponent icon={ReloadSvg} outline size="small" disabled={loading} onClick={handleReload} className={loading && 'box-spin'} /> */}
                    <Breadcrumbs
                        items={[
                            { label: "Home", to: "/app/home" },
                            { label: PageTitle },
                        ]}
                        separator="›"
                        activeLastBreadcrumb={false}
                        size="small"
                        color={'var(--text-color-secondary)'}
                    />
                </div>
            </AuthPageHead>

            {/* LOADING STATE */}
            {
                loading &&
                <LoaderComponent />
            }

            {/* EMPTY STATE */}
            {/* {
                !loading && states.data && states.data.length === 0 &&
                <EmptyTableComponent text='No data found!' />
            } */}

            {/* PAGE CONTENT */}
            {
                <>
                   <PaginationComponent
                        page={states?.pagination?.currentPage}
                        pages={states?.pagination?.totalPages || 1}
                        onPageChange={(e) => handlePagination(e)}
                        canPrevious={states?.pagination?.currentPage > 0}
                        canNext={states?.pagination?.currentPage < (states?.pagination?.totalPages - 1)}
                    />
                    <ReactTableWrapper className="box-shadow custom-scrollbar">
                        {/* <TableComponent rows={states.data} columns={columns} icon={BadgeSvg} />
                    <PaginationComponent data={states.pagination} handleChange={(e) => handlePagination(e)} /> */}
                        <ReactTableWrappers>
                            <ReactTable
                                manual
                                loading={loading}
                                data={states?.data}
                                columns={columns}
                                page={states?.pagination?.currentPage || 0}
                                pageSize={states?.pagination?.pageSize || 20}
                                pages={states?.pagination?.totalPages || 1}
                                filterable={false}
                                showPagination={false}
                                showPageJump={true}
                                showPageSizeOptions={false}
                                showPaginationTop={true}
                                showPaginationBottom={false}
                                onPageChange={(e) => handlePagination(e)}
                                minRows={0}
                            // defaultFilterMethod={(filter, row, column) => {
                            //     return true;
                            // }}
                            // onFilteredChange={(filtered, column, value) => {
                            //     const searchCriteria = transformToObject(filtered);
                            //     setComponentState((prevState) => ({
                            //         ...prevState,
                            //         searchCriteria,
                            //         tableLoading: true,
                            //     }));

                            //     handleOnSearch(searchCriteria); // Trigger handle search
                            // }}
                            />
                        </ReactTableWrappers>
                    </ReactTableWrapper>
                </>
            }

            {/* MODAL */}
            {
                states.isModalOpen &&
                <AddUpdateApiConfigurationModal
                    data={form}
                    label={states?.selected?.operation}
                    institution={states?.selected?.taskDetailRes?.institution}
                    update={states.isUpdate}
                    close={handleModalClose}
                    handleFormSubmit={handleFormSubmit}
                    fromMakerChecker
                    slide
                />
            }
        </AuthPageContainer>
    );
};

export default ApiConfigManagementInbox;